import { ThemeOptions } from '@mui/material/styles';

export const getTheme = (mode: 'light' | 'dark'): ThemeOptions => ({
  direction: 'ltr',
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: '#2196f3', light: '#e3f2fd', dark: '#1e88e5' },
          secondary: { main: '#673ab7', light: '#ede7f6', dark: '#5e35b1' },
          success: { main: '#00e676', light: '#b9f6ca', dark: '#00c853', contrastText: '#ffffff' },
          info: { main: '#2196f3', light: '#e3f2fd', dark: '#1e88e5', contrastText: '#ffffff' },
          error: { main: '#f44336', light: '#ef9a9a', dark: '#c62828', contrastText: '#ffffff' },
          warning: { main: '#ffe57f', light: '#fff8e1', dark: '#ffc107', contrastText: '#212121' },
          grey: {
            50: '#f8fafc',
            100: '#eef2f6',
            200: '#e3e8ef',
            300: '#cdd5df',
            500: '#697586',
            600: '#4b5565',
            700: '#364152',
            900: '#121926',
          },
          text: { primary: '#364152', secondary: '#697586' },
          background: { default: '#f8fafc', paper: '#ffffff' },
          divider: '#e3e8ef',
          action: { disabledBackground: 'rgba(73,82,88,0.12)', hoverOpacity: 0.02, hover: '#f8fafc' },
        }
      : {
          // GitHub dark palette
          primary: { main: '#58a6ff', light: '#1f2f47', dark: '#1f6feb', contrastText: '#ffffff' },
          secondary: { main: '#bc8cff', light: '#2d2140', dark: '#8957e5', contrastText: '#ffffff' },
          success: { main: '#3fb950', light: '#132b1c', dark: '#238636', contrastText: '#ffffff' },
          info: { main: '#58a6ff', light: '#1f2f47', dark: '#1f6feb', contrastText: '#ffffff' },
          error: { main: '#f85149', light: '#3a1c1c', dark: '#da3633', contrastText: '#ffffff' },
          warning: { main: '#d29922', light: '#3a2e12', dark: '#bb8009', contrastText: '#ffffff' },
          grey: {
            50: '#f0f6fc',
            100: '#c9d1d9',
            200: '#b1bac4',
            300: '#8b949e',
            500: '#6e7681',
            600: '#484f58',
            700: '#30363d',
            900: '#0d1117',
          },
          text: { primary: '#c9d1d9', secondary: '#8b949e' },
          background: { default: '#0d1117', paper: '#161b22' },
          divider: '#30363d',
          action: { disabledBackground: 'rgba(139,148,158,0.12)', hoverOpacity: 0.06, hover: '#161b22' },
        }),
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    h1: { fontWeight: 700, fontSize: '2.125rem' },
    h2: { fontWeight: 700, fontSize: '1.5rem' },
    h3: { fontWeight: 600, fontSize: '1.25rem' },
    h4: { fontWeight: 600, fontSize: '1rem' },
    h5: { fontWeight: 500, fontSize: '0.875rem' },
    h6: { fontWeight: 500, fontSize: '0.75rem' },
    button: { textTransform: 'capitalize' },
    body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: '1.334em' },
    body2: { fontSize: '0.75rem', fontWeight: 400, lineHeight: '1.5em' },
    subtitle1: { fontSize: '0.875rem', fontWeight: 500 },
    subtitle2: { fontSize: '0.75rem', fontWeight: 400 },
    caption: { fontSize: '0.75rem', fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { boxSizing: 'border-box' },
        body: { transition: 'background-color 0.3s ease, color 0.3s ease' },
        '.MuiPaper-elevation9, .MuiPopover-root .MuiPaper-elevation': {
          boxShadow:
            'rgb(145 158 171 / 30%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px !important',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: '0px 2px 8px rgba(0,0,0,0.08)' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 12, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          marginBottom: 4,
          '&.Mui-selected': {
            backgroundColor: mode === 'light' ? '#e3f2fd' : '#1f2f47',
            color: mode === 'light' ? '#2196f3' : '#58a6ff',
            '& .MuiListItemIcon-root': { color: mode === 'light' ? '#2196f3' : '#58a6ff' },
            '&:hover': {
              backgroundColor: mode === 'light' ? '#e3f2fd' : '#1f2f47',
            },
          },
          '&:hover': {
            backgroundColor: mode === 'light' ? '#eef2f6' : '#161b22',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: mode === 'light' ? '#e3f2fd' : '#161b22',
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: mode === 'light' ? '#bbdefb' : '#1f2f47',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: mode === 'light' ? '#e3e8ef' : '#30363d',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none !important',
          boxShadow: 'none',
          backgroundColor: mode === 'light' ? '#f8fafc' : '#0d1117',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { boxShadow: 'none' },
      },
    },
  },
});

const CustomTheme = getTheme('light');
export default CustomTheme;
